import React, {
  useEffect,
  useMemo,
  useRef,
  Children,
  isValidElement,
  cloneElement,
  useCallback,
  useState,
  useReducer,
} from 'react'
import {
  useWindowContext,
  Spinner,
  useIsMountedRef,
  setRoute,
  Footer,
  Status,
  Row,
  Col,
  noop, Form, DateInput,
  List, Button, Modal, Table, Paper, Icon, Header,
} from '@iqueue/ui-kit'
import { last, pathOr, prop, path } from 'ramda'
import FactoryCard from '../../components/gasAndOil/card/FactoryCard'
import FieldCard from '../../components/gasAndOil/card/FieldCard'
import AssemblyPointCard from '../../components/gasAndOil/card/AssemblyPointCard'
import WellCard from '../../components/gasAndOil/card/WellCard'
import ErrorView from '../../components/errors/ErrorView'
import StatisticsPresenter from '../../api/statisticsPresenter'
import NodePresenter from '../../api/nodePresenter'
import { UNIT_VOLUME } from '../../components/gasAndOil/Measurement'
import ProductionDiffLine from '../../components/gasAndOil/ProductionDiffLine'
import PressureCard from '../../components/gasAndOil/card/PressureCard'
import TemperatureCard from '../../components/gasAndOil/card/TemperatureCard'
import WellGraph from '../../components/gasAndOil/card/WellGraph'
import moment from 'moment'
import useSocket from '../../hooks/useSocket'
import PickNodeStatus from '../../components/picker/PickNodeStatus'
import useTranslate, { t } from '../../hooks/useTranslate'
import usePermissions from '../../components/usePermissions'
import ResponsibilitiesPresenter from '../../api/responsibilitiesPresenter'
import ViewStatusNodes from '../../components/ViewStatusNodes'
import {
  SCADAView,
  svgText,
  faceNode,
  lineChart,
  thermometer,
  wallScreen,
  tank,
  flareTower,
  people01,
  board,
} from '@amberbeard/scada-view'
import '../../translations'
import cn from 'classnames'

/*
 Обновление главной — раз в час
 Обновление скважин — раз в 5 мин
 */

function BaseSummaryCard({ summary, haveNodes = true, onSummaryClick }) {
  return <>
    <div className={ 'cards' }>
      <div className={ 'gas-and-oil-summary-card' }>
        <div className={ 'content' }>
          <ProductionDiffLine
            percentage={ false }
            current={ pathOr(0, ['VOLUME', 'summary', 'hourlyValue'], summary) }
            past={ pathOr(0, ['VOLUME', 'summary', 'pastHourValue'], summary) }
            unit={ UNIT_VOLUME }
          />
        </div>

        <Footer>
          <div className={ 'iq-spacer' }>
            <b>{ t('productionLabels.hourlyProduction') }</b>
          </div>
        </Footer>
      </div>

      <div className={ 'gas-and-oil-summary-card' }>
        <div className={ 'content' }>
          <ProductionDiffLine
            percentage={ false }
            current={ pathOr(0, ['VOLUME', 'summary', 'dailyValue'], summary) }
            past={ pathOr(0, ['VOLUME', 'summary', 'pastDayValue'], summary) }
            unit={ UNIT_VOLUME }
          />
        </div>

        <Footer>
          <div className={ 'iq-spacer' }>
            <b>{ t('productionLabels.dailyProduction') }</b>
          </div>
        </Footer>
      </div>

      <div className={ 'gas-and-oil-summary-card' }>
        <div className={ 'content' }>
          <ProductionDiffLine
            percentage={ false }
            current={ pathOr(0, ['VOLUME', 'summary', 'monthlyValue'], summary) }
            past={ pathOr(0, ['VOLUME', 'summary', 'pastMonthValue'], summary) }
            unit={ UNIT_VOLUME }
          />
        </div>

        <Footer>
          <div className={ 'iq-spacer' }>
            <b>{ t('productionLabels.monthlyProduction') }</b>
          </div>
        </Footer>
      </div>

      <div className={ 'gas-and-oil-summary-card' }>
        <div className={ 'content' }>
          <ProductionDiffLine
            percentage={ false }
            current={ pathOr(0, ['VOLUME', 'summary', 'yearlyValue'], summary) }
            past={ pathOr(0, ['VOLUME', 'summary', 'pastYearValue'], summary) }
            unit={ UNIT_VOLUME }
          />
        </div>

        <Footer>
          <div className={ 'iq-spacer' }>
            <b>{ t('productionLabels.yearlyProduction') }</b>
          </div>
        </Footer>
      </div>
    </div>

    { haveNodes && <Row breakline={ 'tablet' } align={ 'stretch' }>
      <Col size={ 4 }>
        <div className={ 'gas-and-oil-summary-card' }>
          <div className={ 'content' }>
            <h1>
              <Status
                horizontal
                progress={ summary.operatingFactor }
              >&nbsp;&nbsp;{ Math.round(summary.operatingFactor) }%&nbsp;&nbsp;</Status>
            </h1>
          </div>

          <Footer>
            <div className={ 'iq-spacer' }>
              <b>{ t('productionLabels.operatingFactor') }</b>
            </div>
          </Footer>
        </div>
      </Col>

      <Col size={ 8 }>
        <div className={ 'gas-and-oil-summary-card' }>
          <div className={ 'content summary' }>
            <p>
              <span onClick={ () => onSummaryClick(null) }>
                { t('productionLabels.totalFund') }: <b>
                <Status
                  inline
                  weak
                  accent>
                  { pathOr('??', ['nodes', 'TOTAL'], summary) }
                </Status>
              </b>
              </span>
              &nbsp;&nbsp;&nbsp;

              <span onClick={ () => onSummaryClick('WORKING') }>
                { t('productionLabels.active') }:<b>
                <Status
                  inline
                  weak>
                  { pathOr('??', ['nodes', 'WORKING'], summary) }
                </Status>
              </b>
              </span>
            </p>

            <p>
              <span onClick={ () => onSummaryClick('BLOWING') }>
                { t('productionLabels.blowing') }: <Status
                inline
                weak
                warning>
                { pathOr('??', ['nodes', 'BLOWING'], summary) }
              </Status>
              </span>
              &nbsp;&nbsp;&nbsp;

              <span onClick={ () => onSummaryClick('CONTROL') }>
                { t('productionLabels.control') }: <Status
                inline
                weak
                warning>
                { pathOr('??', ['nodes', 'CONTROL'], summary) }
              </Status>
              </span>
              &nbsp;&nbsp;&nbsp;

              <span onClick={ () => onSummaryClick('STATIC') }>
                { t('productionLabels.static') }: <Status
                inline
                weak
                warning>
                { pathOr('??', ['nodes', 'STATIC'], summary) }
              </Status>
              </span>
              &nbsp;&nbsp;&nbsp;

              <span onClick={ () => onSummaryClick('DOWNTIME') }>
                { t('productionLabels.downtime') }: <Status
                inline
                weak
                error>
                { pathOr('??', ['nodes', 'DOWNTIME'], summary) }
              </Status>
              </span>
              &nbsp;&nbsp;&nbsp;

              <span onClick={ () => onSummaryClick('TECH_WORKS') }>
                { t('productionLabels.maintenance') }: <Status
                inline
                weak
                error>
                { pathOr('??', ['nodes', 'TECH_WORKS'], summary) }
              </Status>
              </span>
              &nbsp;&nbsp;&nbsp;

              <span onClick={ () => onSummaryClick('DRILLING') }>
                { t('productionLabels.drilling') }: <Status
                inline
                weak
                error>
                { pathOr('??', ['nodes', 'DRILLING'], summary) }
              </Status>
              </span>
              &nbsp;&nbsp;&nbsp;

              <span onClick={ () => onSummaryClick('LIQUIDATION') }>
                { t('productionLabels.liquidation') }: <Status
                inline
                weak
                error>
                { pathOr('??', ['nodes', 'LIQUIDATION'], summary) }
              </Status>
              </span>
            </p>
          </div>

          <Footer>
            <div className={ 'iq-spacer' }>
              <b>{ t('productionLabels.wellFund') }</b>
            </div>
          </Footer>
        </div>
      </Col>
    </Row> }
  </>
}

function CollectionPointSummaryCard({ summary }) {
  return <>
    <Row breakline={ 'laptop' }>
      <Col size={ 6 }>
        { summary?.sensors?.some(sensor => sensor.type === 'PRESSURE_INPUT') && (
          <PressureCard
            summary={ summary }
            // title="Давление на входе"
            title={ t('type.sensor.PRESSURE_INPUT') }
            scope="PRESSURE_INPUT"
          />
        ) }
      </Col>
      <Col size={ 6 }>
        { summary?.sensors?.some(sensor => sensor.type === 'PRESSURE_OUTPUT') && (
          <PressureCard
            summary={ summary }
            // title="Давление на выходе"
            title={ t('type.sensor.PRESSURE_OUTPUT') }
            scope="PRESSURE_OUTPUT"
          />
        ) }
      </Col>
    </Row>
  </>
}

function WellPointSummaryCard({ summary }) {
  const socket = useSocket()
  const [openStatusNode, setOpenStatusNode] = useState(false)
  const [currentNode, setCurrentNode] = useState({})
  const [updatedSummary, setUpdatedSummary] = useState()
  const [chartData, setChartData] = useState()
  const [currentNodeValue, setCurrentNodeValue] = useState(null)
  const [selectPerson, setSelectPerson] = useState(null)
  const [openModal, setOpenModal] = useState(false)

  const { permissions } = usePermissions()

  const [responsibility, setResponsibility] = useState([])

  const [date, setDate] = useState({
    from: moment().startOf('day'),
    to: moment().add(1, 'day').endOf('day'),
  })

  const updateCurrentNode = useCallback(async () => {
    await NodePresenter.get(`${ summary.id }`).then(setCurrentNode)
  }, [summary])

  // MAX CREATE
  const handleGetResponsibilities = useCallback(async () => {
    const data = await ResponsibilitiesPresenter.get('', {
      f: {
        nodeId: {
          eq: summary.id,
        },
      },
    })
    setResponsibility(data)
  }, [])

  useEffect(() => {
    if (date.from) {
      setDate((prevDate) => ({ ...prevDate, from: date.from }))
    }
    if (date.to) {
      setDate((prevDate) => ({ ...prevDate, to: date.to }))
    }
  }, [date.from, date.to])

  useEffect(() => {
    handleGetResponsibilities().catch(err => console.error(err))
  }, [])

  useEffect(() => {
    if (!socket) return
    const handleSensorEvent = (data) => {
      if (data.type === 'PRESSURE_PLUME' || data.type === 'TEMPERATURE') {
        setUpdatedSummary(prevSummary => {
          const sensorIndex = prevSummary.sensors.findIndex(sensor => sensor.id === data.id)

          if (sensorIndex !== -1) {
            const updatedSensors = [...prevSummary.sensors]
            updatedSensors[sensorIndex] = {
              ...updatedSensors[sensorIndex],
              state: {
                ...updatedSensors[sensorIndex].state,
                value: data.state.value,
              },
              status: data.status,
              updatedAt: data.updatedAt,
            }

            return {
              ...prevSummary,
              sensors: updatedSensors,
            }
          }

          return prevSummary
        })
      }
    }

    socket.on('sensors/event', handleSensorEvent)
    return () => {
      socket.off('sensors/event', handleSensorEvent)
    }
  }, [socket])

  useEffect(() => {
    if (!socket) return

    const handleSensorEvent = (data) => {
      setUpdatedSummary(prevSummary => {
        const sensorIndex = prevSummary.sensors.findIndex(sensor => sensor.id === data.id)

        if (sensorIndex !== -1) {
          const updatedSensors = [...prevSummary.sensors]

          updatedSensors[sensorIndex] = {
            ...updatedSensors[sensorIndex],
            ...data,
          }

          return {
            ...prevSummary,
            sensors: updatedSensors,
          }
        }

        return prevSummary
      })
    }

    socket.on('sensors/update', handleSensorEvent)
    return () => {
      socket.off('sensors/update', handleSensorEvent)
    }
  }, [socket])

  useEffect(() => {
    updateCurrentNode().catch(err => console.error(err))
  }, [])

  useEffect(() => {
    setUpdatedSummary(summary)
  }, [])

  return <>
    <Row breakline={ 'desktop' }>
      <Col size={ 4 }>
        <div className={ 'gas-and-oil-summary-card status-well' }>
          <div className={ 'status-well-children' }>
            <ViewStatusNodes item={ currentNode } filed={ true } currentValue={ currentNodeValue }/>
          </div>
          { permissions?.NODE?.includes('UPDATE_STATUS') && <Button
            secondary
            onClick={ () => {
              setOpenStatusNode(true)
            } }
            className={ 'node-edit' }
            icon={ 'edit' }
          /> }
          <Footer>
            <div className={ 'iq-spacer' }>
              <b>{ t('wellInfo.status') }</b>
            </div>
          </Footer>
        </div>
      </Col>

      <Col size={ 4 }>
        <div className={ 'gas-and-oil-summary-card' }>
          <div className={ 'content main-info-card' }>
            <h5>{ t('nodes.plumeDiameter') }: <Status
              inline
              accent
              weak>
              &nbsp;&nbsp;
              { currentNode?.meta?.wellTechnicalInformation?.plumeDiameter || 'N/A' }
              &nbsp;&nbsp;
            </Status>
            </h5>

            <h5>{ t('nodes.plumeLength') }: <Status
              inline
              accent
              weak>
              &nbsp;&nbsp;
              { currentNode?.meta?.wellTechnicalInformation?.plumeLength || 'N/A' }
              &nbsp;&nbsp;
            </Status>
            </h5>

            <h5>{ t('nodes.pipeThickness') }: <Status
              inline
              accent
              weak>
              &nbsp;&nbsp;
              { currentNode?.meta?.wellTechnicalInformation?.pipeThickness || 'N/A' }
              &nbsp;&nbsp;
            </Status>
            </h5>
          </div>

          <Footer>
            <div className={ 'iq-spacer' }>
              <b>{ t('wellInfo.technicalInfo') }</b>
            </div>
          </Footer>
        </div>
      </Col>
      <Col size={ 4 }>
        <div className={ 'gas-and-oil-summary-card exploitation-card' }>
          <div className={ 'content' }>
            <h1>
              <Status
                horizontal
                progress={ summary.operatingFactor }
              >
                &nbsp;&nbsp;
                { Math.round(summary.operatingFactor) }%&nbsp;&nbsp;
              </Status>
            </h1>
          </div>

          <Footer>
            <div className={ 'iq-spacer' }>
              <b>{ t('productionLabels.operatingFactor') }</b>
            </div>
          </Footer>
        </div>
      </Col>
    </Row>
    <Row breakline={ 'desktop' }>
      <Col size={ 4 }>
        { updatedSummary?.sensors.some(sensor => sensor.type === 'PRESSURE_WELL') && (
          <PressureCard
            summary={ updatedSummary }
            title={ t('type.sensor.PRESSURE_WELL') }
            scope="PRESSURE_WELL"
          />
        ) }
      </Col>
      <Col size={ 4 }>
        { updatedSummary?.sensors.some(sensor => sensor.type === 'PRESSURE_PLUME') && (
          <PressureCard
            summary={ updatedSummary }
            title={ t('type.sensor.PRESSURE_PLUME') }
            scope="PRESSURE_PLUME"
          />
        ) }
      </Col>
      <Col size={ 4 }>
        { updatedSummary?.sensors.some(sensor => sensor.type === 'TEMPERATURE') && (
          <TemperatureCard
            summary={ updatedSummary }
            title={ t('type.sensor.TEMPERATURE') }
            scope="TEMPERATURE"
          />
        ) }
      </Col>
    </Row>

    <Row breakline={ 'desktop' }>
      <Col size={ 4 }>
        <div className={ 'gas-and-oil-summary-card' }>
          <List
            className={ 'list-of-responsible-person' }
            items={ responsibility || [] }
            selected={ responsibility }
            selectionMode={ 'item' }
            onSelect={ (item) => {
              setSelectPerson(item)
              setOpenModal(true)
            } }
            render={ (item) => ({
              id: item?.profile?.id,
              avatar: item?.profile?.user?.username.charAt(0).toUpperCase(),
              title: item?.profile?.name + ' ' + item?.profile?.surname,
              subtitle: item?.profile?.phoneNumber,
            }) }
          />

          <Modal
            isOpened={ openModal }
            title={ t('personInfoModal.title') }
            footerActions={ [
              { key: 'close', danger: true, title: t('modal.editWellStatusModal.close') },
            ] }
            onClose={ () => setOpenModal(false) }
          >
            <div className="person-info-modal">
              <div className="info-block">
                <div className="label">{ t('personInfoModal.nameLabel') }</div>
                <div className="info-card">
                  <div className="value">
                    { [
                      selectPerson?.profile?.surname,
                      selectPerson?.profile?.name,
                      selectPerson?.profile?.patronymic,
                    ].filter(Boolean).join(' ') || '—' }
                  </div>
                </div>
              </div>

              <div className="info-block">
                <div className="label">{ t('personInfoModal.dobLabel') }</div>
                <div className="info-card">
                  <div className="value">
                    { selectPerson?.profile?.dateOfBirth
                      ? moment(selectPerson?.profile?.dateOfBirth).format('YYYY-MM-DD')
                      : '—' }
                  </div>
                </div>
              </div>

              <div className="info-block">
                <div className="label">{ t('personInfoModal.phoneLabel') }</div>
                <div className="info-card">
                  <div className="value">
                    { selectPerson?.profile?.phoneNumber ? (
                      <a href={ `tel:${ selectPerson.profile.phoneNumber }` } className="phone-link">
                        { selectPerson.profile.phoneNumber }
                      </a>
                    ) : (
                      '—'
                    ) }
                  </div>
                </div>
              </div>
            </div>

          </Modal>

          <Footer>
            <div className={ 'iq-spacer' }>
              <b>{ t('wellInfo.responsiblePersons') }</b>
            </div>
          </Footer>
        </div>
        { summary?.instantaneousWellFlowRate && (
          <div className="left-information">
            <h5>{ t('wellInfo.instantaneousFlowRate') }:</h5>
            <b>
              { summary?.instantaneousWellFlowRate } { summary?.instantaneousWellFlowRate ? t('wellInfo.unit') : '' }
            </b>
          </div>
        ) }
      </Col>
      <Col size={ 8 }>
        <WellGraph
          summary={ !chartData ? summary : chartData }
        />
        <div className={ 'well-graph-date' }>
          <Form onSubmit={ (item) => {
            item.from.toISOString()
            item.to.toISOString()

            StatisticsPresenter.get(`nodes/summary/${ summary.id }`, {
              from: item.from.toISOString(),
              to: item.to.toISOString(),
            }).then(setChartData)
          } }>
            <Row>
              <DateInput
                size={ 8 }
                name={ 'from' }
                placeholder={ t('dateChart.datePlaceholder.from') }
                value={ date.from }
                onChange={ (from) => {
                  const toStartOfDay = from.startOf('day')
                  setDate((prevDate) => ({ ...prevDate, toStartOfDay }))
                } }
                max={ moment().startOf('day') }
              />

              <DateInput
                size={ 8 }
                name={ 'to' }
                placeholder={ t('dateChart.datePlaceholder.to') }
                value={ date.to }
                onChange={ (to) => {
                  const toEndOfDay = to.endOf('day')
                  setDate((prevDate) => ({ ...prevDate, toEndOfDay }))
                } }
                max={ moment().endOf('day') }
              />

              <Button
                submit
                size={ 4 }
                secondary
                title={ t('dateChart.apply') }
              />
            </Row>
          </Form>
        </div>
      </Col>
    </Row>

    <Modal
      title={ t('modal.editWellStatusModal.title') }
      isOpened={ openStatusNode }
      onClose={ () => setOpenStatusNode(false) }
      onApply={ async (item) => {
        setCurrentNodeValue(item.status)
        await NodePresenter.patch(`update-status/${ summary.id }`, { status: item.status })
        setCurrentNode({ ...currentNode, status: item.status })
      } }
      footerActions={ [
        { key: 'cancel', title: t('modal.editWellStatusModal.close') },
        { key: 'delete', title: t('modal.editWellStatusModal.apply'), submit: true, primary: true },
      ] }
    >
      <Row>
        <PickNodeStatus
          placeholder={ t('modal.editWellStatusModal.wellStatusPlaceholder') }
          name={ 'status' }
          size={ 12 }
          value={ currentNodeValue || currentNode?.status }
        />
      </Row>
    </Modal>
  </>
}

const STATUS = {
  WORKING: {},
  TOTAL: { accent: true },
  BLOWING: { warning: true },
  CONTROL: { warning: true },
  STATIC: { warning: true },
  DOWNTIME: { error: true },
  TECH_WORKS: { error: true },
  DRILLING: { error: true },
  LIQUIDATION: { error: true },
}

function ProductionSummaryCard({ summary, path }) {
  const locale = useTranslate()

  const optimized = useMemo(() => {
    return {
      ...summary,
      node: summary.node,
      operatingFactor: summary.operatingFactor ? summary.operatingFactor * 100 : 0,
      title: t(`summary.${ summary.type }`, { name: summary.name }),
    }
  }, [summary, locale])

  const schema = useMemo(() => {
    return [{
      key: 'name',
      title: t('productionPage.table.name'),
      render: (name) => name,
    }, {
      key: 'type',
      title: t('productionPage.table.type'),
      center: true,
      render: (type) => t(`type.node.${ type }`),
    }, {
      key: 'status',
      title: t('sensorInfo.sensorStatus'),
      render: (status) => {
        return <Status
          inline
          weak
          { ...STATUS[status] }
        >
          { t(`status.node.${ status }`) }
        </Status>
      },
    }, {
      key: 'createdAt',
      title: t('productionPage.table.createdAt'),
      center: true,
      render: (createdAt) => moment(createdAt).format('LLL'),
    }, {
      key: 'updatedAt',
      title: t('productionPage.table.updatedAt'),
      center: true,
      render: (updatedAt) => moment(updatedAt).format('LLL'),
    }]
  }, [t, locale])

  const summaryModal = useCallback(async (status) => {
    const filter = {
      f: {
        ...(!status ? {} : {
          status: {
            eq: status,
          },
        }),
        type: {
          eq: 'WELL',
        },
      },
    }

    const content = async () => NodePresenter.listNodes(filter, { allowDots: true })

    try {
      await Modal.show({
        title: path?.title || ' ',
        className: 'production-summary-modal',
        fullscreen: true,
        content,
        children: (entries) => <Table
          indexable
          entries={ entries }
          schema={ schema }
          filter={ {} }
          minWidth={ '130rem' }
        />,
      })
    } catch (e) {
      console.error(e)
    }
  }, [schema, path])

  return <>
    <div className={ 'download-box' }>
      <h4>{ optimized.title }</h4>
    </div>
    <p/>

    { summary.type === 'COLLECTION_POINT' && <CollectionPointSummaryCard summary={ optimized }/> }
    { summary.type === 'WELL' && <WellPointSummaryCard summary={ optimized }/> }
    { summary.type !== 'WELL' && <BaseSummaryCard onSummaryClick={ summaryModal } summary={ optimized }/> }
  </>
}

function ProductionCards({
  children,
  showSpinner = false,
  showError = false,
  spinnerRadius,
  errorClassName,
  request,
}) {
  const [data, setData] = useState()
  const [error, setError] = useState()
  const { route } = useWindowContext()
  const [tick, forceUpdate] = useReducer(x => x + 1, 0)

  const parentId = useMemo(() => {
    const parts = route.split('/')
    if (parts.length === 1) return undefined

    return last(parts)
  }, [route])

  const onCardSelected = useCallback((childId) => {
    if (showSpinner) {
      setData(undefined)
    }

    setRoute(['production', childId].join('/'))
  }, [route, showSpinner])

  useEffect(() => {
    if (!request) return
    noop(tick)

    let isMounted = true
    if (showSpinner) {
      setData(undefined)
      setError(undefined)
    }

    request(parentId)
      .then(newData => {
        if (!isMounted) return
        setError(undefined)
        setData(newData)
      })
      .catch(err => {
        if (!isMounted) return
        if (showError) {
          setError(err)
        } else {
          setData(null)
        }
      })

    return () => {
      isMounted = false
    }
  }, [request, parentId, showError, showSpinner, tick])

  if (error) {
    return <ErrorView
      error={ error }
      className={ errorClassName }
      refetch={ forceUpdate }
    />
  }

  if (data === undefined) {
    return <Spinner
      radius={ spinnerRadius }
    />
  }

  return Children.map(children, child => {
    if (isValidElement(child)) {
      return cloneElement(child, {
        data,
        // parentId,
        onCardSelected,
      })
    }

    return child
  })
}

const schema = {
  schema: {
    containerWidth: 1000,
    // containerHeight: 768,
    pipeDepthOffset: 3,
    // isometricAngle: 20,
  },
  nodes: [
    faceNode({
      id: 'floor',
      size: { width: 11, depth: 10, height: 0 },
      position: { x: -3, y: -4, z: 0 },
      plane: 'XY',
      drawOrder: -1000,
      faceOptions: {
        rx: 12,
        fill: '{{theme.nodeStroke}}',
      },
    }),

    {
      id: 'adScreen',
      type: 'custom',
      position: { x: 4, y: -4, z: 0 },
      size: { width: 3, depth: 0.2 * 3, height: 3 },
      metadata: {
        svgTemplate: {
          markup: '',
          markupIso: `<g transform="scale(3)">
              <g transform="scale(1.3)translate(-20,-46)">
                ${ wallScreen }
              </g>
            </>`,
        },
      },
    },

    faceNode({
      id: 'screen',
      size: { width: 2.4, depth: 0, height: 2.4 },
      position: { x: 4.34, y: -3.5, z: 0.58 },
      plane: 'YZ',
      drawOrder: 5,
      faceOptions: {
        rx: 0,
        fill: '{{theme.primary}}',
      },
      children: lineChart({
        name: 'Температура в цистерне',
        suffix: ' °C',
        padding: 4,
        color: '{{theme.typeA}}',
        background: '{{theme.muted}}',
        history: [
          12.7,
          34.2,
          18.9,
          27.5,
          39.1,
          15.4,
          22.8,
          31.6,
          10.9,
          24.3,
          37.8,
          19.6,
          28.1,
          16.2,
          33.4,
          21.7,
          14.8,
          29.9,
          11.5,
          35.6,
          26.4,
          17.3,
          38.2,
          23.1,
          30.7,
          13.6,
          32.5,
          20.4,
          36.9,
          25.2, // 10 - 40
        ],
        width: 2.4 * 48 - 8,
        height: (2.4 * 48 - 16) / 3,
        y: -(2.4 * 48 - 6) / 3,
      }) + lineChart({
        name: 'Расход',
        suffix: ' м³/ч',
        padding: 4,
        color: '{{theme.typeB}}',
        background: '{{theme.muted}}',
        history: [
          1456,
          2987,
          2103,
          3345,
          1789,
          2567,
          3124,
          1890,
          1402,
          2733,
          3210,
          1678,
          2954,
          2301,
          3492,
          1523,
          2844,
          1987,
          2415,
          3076,
          1734,
          2650,
          3189,
          1204,
          2901,
          2248,
          3367,
          1599,
          2754,
          3431, // 1000 - 4000
        ],
        width: 2.4 * 48 - 8,
        height: (2.4 * 48 - 16) / 3,
      }) + lineChart({
        name: 'Рабочее давление',
        suffix: ' кГс/см²',
        padding: 4,
        color: '{{theme.typeC}}',
        background: '{{theme.muted}}',
        history: [
          11.4,
          42.7,
          19.3,
          34.8,
          27.1,
          15.9,
          38.6,
          21.4,
          44.2,
          30.5,
          17.8,
          25.6,
          40.9,
          13.2,
          36.4,
          28.7,
          23.9,
          31.3,
          14.6,
          41.5,
          29.8,
          18.5,
          33.1,
          12.9,
          37.2,
          26.0,
          43.6,
          20.7,
          35.4,
          24.8, // 10-45
        ],
        width: 2.4 * 48 - 8,
        height: (2.4 * 48 - 16) / 3,
        y: (2.4 * 48 - 6) / 3,
      }),
    }),

    {
      id: 'tank',
      type: 'custom',
      position: { x: 0, y: 0, z: 0 },
      size: { width: 6, depth: 3, height: 3 },
      // size: { width: 2, depth: 1, height: 1 },
      drawOrder: 5,
      metadata: {
        svgTemplate: {
          markup: '',
          markupIso: `<g transform="scale(3)">
              <g transform="scale(1.7)translate(-43.3,-44)">
                ${ tank }
              </g>
            </>`,
        },
      },
    },

    faceNode({
      id: 'tank-level',
      plane: '2D',
      position: { worldX: 350, worldY: 90 },
      size: { width: 2, depth: 0, height: 2 },
      faceOptions: {
        fill: '{{theme.primary}}',
      },
      // drawOrder: 9999,
      children: thermometer({
        x: 16,
        y: 8,
        width: 48 - 8,
        height: 48 * 1.5 - 8,
        min: 0,
        max: 100,
        valueYTag: 'fuelYTag',
        valueHeightTag: 'fuelHeightTag',
        color: '{{theme.nodeSuccess}}',
        ticksColor: '{{theme.nodeStroke}}',
        bgColor: '{{theme.nodeInactive}}',
      }) + svgText('Уровень конденсата', {
        y: -34,
        size: 8,
        anchor: 'middle',
      }) + svgText('{{value}}%', {
        x: -24,
        y: 16,
        size: 16,
        anchor: 'middle',
      }),
      placeholders: {
        fuelYTag: ({ nodeData = {} }) => {
          const fillLevel = (nodeData?.fillLevel) ?? 0
          const clamped = Math.min(Math.max(fillLevel, 0), 1)

          const x = (48 * 1.5 - 8) - 1

          return x - (x * clamped)
        },
        fuelHeightTag: ({ nodeData = {} }) => {
          const fillLevel = (nodeData?.fillLevel) ?? 0
          const clamped = Math.min(Math.max(fillLevel, 0), 1)

          const x = (48 * 1.5 - 8) - 1

          return x * clamped
        },
        // valueYTag: ({ nodeData = {} }) => {
        //   const { temperature = 0, tempMin = 0, tempMax = 100 } = nodeData as {
        //     temperature: number,
        //     tempMin: number,
        //     tempMax: number,
        //   }
        //
        //   const level = (temperature - tempMin) / (tempMax - tempMin)
        //   const clamped = Math.min(Math.max(level, 0), 1)
        //
        //   const x = (48 * 3 - 8) - 1
        //
        //   console.log('Y', x - (x * clamped))
        //   return x - (x * clamped)
        // },
        // valueHeightTag: ({ nodeData = {} }) => {
        //   const { temperature = 0, tempMin = 0, tempMax = 100 } = nodeData as {
        //     temperature: number,
        //     tempMin: number,
        //     tempMax: number,
        //   }
        //
        //   const level = (temperature - tempMin) / (tempMax - tempMin)
        //   const clamped = Math.min(Math.max(level, 0), 1)
        //
        //   const x = (48 * 3 - 8) - 1
        //
        //   console.log('H', x - (x * clamped))
        //   return x * clamped
        // },
        value: ({ nodeData }) => {
          const v = (nodeData?.fillLevel) ?? 0

          return Math.round(v * 100)
        },
      },
    }),

    faceNode({
      id: 'label-1',
      plane: 'XY',
      position: { x: 6.5, y: 3.25, z: 0.5 },
      size: { width: 1, depth: 0.5, height: 0 },
      faceOptions: {
        fill: '{{theme.muted}}',
      },
      children: svgText('Выход →', {
        y: -3,
        size: 8,
        anchor: 'middle',
      }) + svgText('Газ', {
        y: 7,
        size: 8,
        anchor: 'middle',
      }),
    }),

    faceNode({
      id: 'label-2',
      plane: 'XY',
      position: { x: 6.5, y: 2.25, z: 0.5 },
      size: { width: 1, depth: 0.5, height: 0 },
      faceOptions: {
        fill: '{{theme.muted}}',
      },
      children: svgText('Выход →', {
        y: -3,
        size: 8,
        anchor: 'middle',
      }) + svgText('Конденсат', {
        y: 7,
        size: 8,
        anchor: 'middle',
      }),
    }),

    faceNode({
      id: 'label-3',
      plane: 'XY',
      position: { x: -1.5, y: -1.75, z: 0.5 },
      size: { width: 1, depth: 0.5, height: 0 },
      faceOptions: {
        fill: '{{theme.muted}}',
      },
      children: svgText('Вход →', {
        y: -3,
        size: 8,
        anchor: 'middle',
      }) + svgText('Газ', {
        y: 7,
        size: 8,
        anchor: 'middle',
      }),
    }),

    faceNode({
      id: 'label-4',
      plane: 'XY',
      position: { x: -0.75, y: 4.25, z: 0 },
      size: { width: 1, depth: 0.5, height: 0 },
      faceOptions: {
        fill: '{{theme.success}}',
      },
      children: svgText('Дежурный', {
        y: -3,
        size: 8,
        weight: 700,
        color: '{{theme.background}}',
        anchor: 'middle',
      }) + svgText('режим', {
        y: 7,
        size: 8,
        weight: 700,
        color: '{{theme.background}}',
        anchor: 'middle',
      }),
    }),

    {
      id: 'flare-tower-1',
      type: 'custom',
      position: { x: -2, y: 4, z: 0 },
      size: { width: 1, depth: 1, height: 7 },
      metadata: {
        svgTemplate: {
          markup: '',
          markupIso: flareTower,
        },
      },
    },

    {
      id: 'people-1',
      type: 'custom',
      position: { x: 3, y: -4, z: 0 },
      size: { width: 0.5, depth: 1, height: 2 },
      metadata: {
        svgTemplate: {
          markup: '',
          markupIso: people01,
        },
      },
    },

    {
      id: 'pipe-1-temp',
      type: 'custom',
      drawOrder: 10,
      position: { x: -1, y: 3.5, z: 0.64 },
      size: { width: 1, depth: 0.04, height: 1 },
      metadata: {
        svgTemplate: {
          markup: '',
          markupIso: board,
        },
      },
    },

    faceNode({
      id: 'pipe-1-temp-data',
      size: { width: 0.96, depth: 0, height: 0.96 },
      position: { x: -0.98, y: 3.54, z: 0.66 },
      plane: 'YZ',
      drawOrder: 15,
      faceOptions: {
        rx: 0,
        fill: '{{theme.warning}}',
      },
      children: svgText('Температура', {
        y: -4,
        size: 7,
        weight: 500,
        anchor: 'middle',
      }) + svgText(`<tspan>37</tspan>
      <tspan
      font-size="${ 10 * 0.6 }"
      opacity="0.85"
        > °C</tspan>`, {
        y: 12,
        size: 10,
        weight: 700,
        anchor: 'middle',
      }),
    }),

    {
      id: 'pipe-1-flow',
      type: 'custom',
      drawOrder: 10,
      position: { x: 1, y: 3.5, z: 0.64 },
      size: { width: 1, depth: 0.04, height: 1 },
      metadata: {
        svgTemplate: {
          markup: '',
          markupIso: board,
        },
      },
    },

    faceNode({
      id: 'pipe-1-flow-data',
      size: { width: 0.96, depth: 0, height: 0.96 },
      position: { x: 1.02, y: 3.54, z: 0.66 },
      plane: 'YZ',
      drawOrder: 15,
      faceOptions: {
        rx: 0,
        fill: '{{theme.success}}',
      },
      children: svgText('Расход', {
        y: -4,
        size: 7,
        weight: 500,
        anchor: 'middle',
      }) + svgText(`<tspan>1 278</tspan>
      <tspan
      font-size="${ 10 * 0.6 }"
      opacity="0.85"
        > м³/ч</tspan>`, {
        y: 12,
        size: 10,
        weight: 700,
        anchor: 'middle',
      }),
    }),

    {
      id: 'pipe-2-flow',
      type: 'custom',
      drawOrder: 10,
      position: { x: 4, y: 2.5, z: 0.64 },
      size: { width: 1, depth: 0.04, height: 1 },
      metadata: {
        svgTemplate: {
          markup: '',
          markupIso: board,
        },
      },
    },

    faceNode({
      id: 'pipe-2-flow-data',
      size: { width: 0.96, depth: 0, height: 0.96 },
      position: { x: 4.02, y: 2.54, z: 0.66 },
      plane: 'YZ',
      drawOrder: 15,
      faceOptions: {
        rx: 0,
        fill: '{{theme.error}}',
      },
      children: svgText('Расход', {
        y: -4,
        size: 7,
        weight: 500,
        anchor: 'middle',
      }) + svgText(`<tspan>839</tspan>
      <tspan
      font-size="${ 10 * 0.6 }"
      opacity="0.85"
        > м³/ч</tspan>`, {
        y: 12,
        size: 10,
        weight: 700,
        anchor: 'middle',
      }),
    }),

    {
      id: 'pipe-3-temp',
      type: 'custom',
      drawOrder: 25,
      position: { x: 0, y: -1.5, z: 0.64 },
      size: { width: 1, depth: 0.04, height: 1 },
      metadata: {
        svgTemplate: {
          markup: '',
          markupIso: board,
        },
      },
    },

    faceNode({
      id: 'pipe-3-temp-data',
      size: { width: 0.96, depth: 0, height: 0.96 },
      position: { x: 0.02, y: -1.46, z: 0.66 },
      plane: 'YZ',
      drawOrder: 26,
      faceOptions: {
        rx: 0,
        fill: '{{theme.success}}',
      },
      children: svgText('Температура', {
        y: -4,
        size: 7,
        weight: 500,
        anchor: 'middle',
      }) + svgText(`<tspan>21</tspan>
      <tspan
      font-size="${ 10 * 0.6 }"
      opacity="0.85"
        > °C</tspan>`, {
        y: 12,
        size: 10,
        weight: 700,
        anchor: 'middle',
      }),
    }),

    {
      id: 'pipe-3-flow',
      type: 'custom',
      drawOrder: 25,
      position: { x: 2, y: -1.5, z: 0.64 },
      size: { width: 1, depth: 0.04, height: 1 },
      metadata: {
        svgTemplate: {
          markup: '',
          markupIso: board,
        },
      },
    },

    faceNode({
      id: 'pipe-3-flow-data',
      size: { width: 0.96, depth: 0, height: 0.96 },
      position: { x: 2.02, y: -1.46, z: 0.66 },
      plane: 'YZ',
      drawOrder: 26,
      faceOptions: {
        rx: 0,
        fill: '{{theme.success}}',
      },
      children: svgText('Расход', {
        y: -4,
        size: 7,
        weight: 500,
        anchor: 'middle',
      }) + svgText(`<tspan>2117</tspan>
      <tspan
      font-size="${ 10 * 0.6 }"
      opacity="0.85"
        > м³/ч</tspan>`, {
        y: 12,
        size: 10,
        weight: 700,
        anchor: 'middle',
      }),
    }),
  ],
  pipes: [
    {
      id: 'pipe-1',
      color: 'typeA',
      animated: true,
      animationSpeed: 125,
      edges: [
        { from: { x: -1, y: 1, z: 1 }, to: { x: -2, y: 1, z: 1 } },
        { from: { x: -2, y: 1, z: 1 }, to: { x: -2, y: 1, z: 0 } },
        { from: { x: -2, y: 1, z: 0 }, to: { x: -2, y: 3, z: 0 } },
        { from: { x: -2, y: 3, z: 0 }, to: { x: 6, y: 3, z: 0 } },
      ],
    },
    {
      id: 'pipe-2',
      color: 'typeB',
      animated: true,
      drawOrder: -1,
      // drawOrder: 100,
      animationSpeed: 125,
      edges: [
        { from: { x: 1, y: 1, z: 1 }, to: { x: 1, y: 1, z: 0 } },
        { from: { x: 1, y: 1, z: 0 }, to: { x: 1, y: 2, z: 0 } },
        { from: { x: 1, y: 2, z: 0 }, to: { x: 6, y: 2, z: 0 } },
      ],
    },
    {
      id: 'pipe-3',
      color: 'typeC',
      animated: true,
      drawOrder: 10,
      animationSpeed: 125,
      edges: [
        { from: { x: 6, y: 1, z: 1 }, to: { x: 5, y: 1, z: 1, skipTile: true } },
        { from: { x: 6, y: 1, z: 0 }, to: { x: 6, y: 1, z: 1 } },
        { from: { x: 6, y: -2, z: 0 }, to: { x: 6, y: 1, z: 0 } },
        { from: { x: -1, y: -2, z: 0 }, to: { x: 6, y: -2, z: 0 } },
      ],
    },
  ],
}

function El({
  data: {
    nodes = [],
  } = {},
  onCardSelected,
}) {
  const renderProductionCard = useCallback((node) => {
    const onClick = () => onCardSelected(node.id)

    switch (node.type) {
      case 'ORGANIZATION':
      case 'FACTORY':
        return <FactoryCard
          name={ node.name }
          key={ node.id }
          type={ node.type }
          summary={ path(['VOLUME', 'summary'], node) }
          nodes={ prop('nodes', node) }
          hourlyChart={ Object.values(pathOr({}, ['VOLUME', 'chart'], node)) }
          onClick={ onClick }
        />
      case 'COLLECTION_POINT':
        return <AssemblyPointCard
          name={ node.name }
          key={ node.id }
          type={ node.type }
          nodes={ prop('nodes', node) }
          pressureInputHourlyChart={ Object.values(pathOr({}, ['PRESSURE_INPUT', 'chart'], node)) }
          pressureInputSummary={ path(['PRESSURE_INPUT', 'summary'], node) }
          pressureOutputHourlyChart={ Object.values(pathOr({}, ['PRESSURE_OUTPUT', 'chart'], node)) }
          pressureOutputSummary={ path(['PRESSURE_OUTPUT', 'summary'], node) }
          temperatureHourlyChart={ Object.values(pathOr({}, ['TEMPERATURE', 'chart'], node)) }
          temperatureSummary={ path(['TEMPERATURE', 'summary'], node) }
          summary={ path(['VOLUME', 'summary'], node) }
          onClick={ onClick }
        />
      case 'WELL':
        return <WellCard
          name={ node.name }
          key={ node.id }
          type={ node.type }
          nodes={ prop('nodes', node) }
          pressurePlumeHourlyChart={ Object.values(pathOr({}, ['PRESSURE_PLUME', 'chart'], node)) }
          pressurePlumeSummary={ path(['PRESSURE_PLUME', 'summary'], node) }
          pressureWellHourlyChart={ Object.values(pathOr({}, ['PRESSURE_WELL', 'chart'], node)) }
          pressureWellSummary={ path(['PRESSURE_WELL', 'summary'], node) }
          temperatureHourlyChart={ Object.values(pathOr({}, ['TEMPERATURE', 'chart'], node)) }
          temperatureSummary={ path(['TEMPERATURE', 'summary'], node) }
          operatingFactor={ prop('operatingFactor', node) }
          onClick={ onClick }
        />
      case 'FIELD':
        return <FieldCard
          name={ node.name }
          key={ node.id }
          type={ node.type }
          summary={ path(['VOLUME', 'summary'], node) }
          nodes={ prop('nodes', node) }
          hourlyChart={ Object.values(pathOr({}, ['VOLUME', 'chart'], node)) }
          onClick={ onClick }
        />
    }
  }, [onCardSelected])

  if (!nodes.length) return false

  return nodes.map(renderProductionCard)
}

export default function ProductionPage() {
  const isMounted = useIsMountedRef()

  const [crumbs, setCrumbs] = useState([])
  const [summary, setSummary] = useState(null)
  const { togglePaddings, toggleScrollbars, setTitle, withBreadcrumbs, route, breadcrumb } = useWindowContext()
  const { tree } = usePermissions()
  const locale = useTranslate()
  const translatedTitle = useMemo(() => t('menu.production'), [locale])
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isDebug, setIsDebug] = useState(false)
  const fullscreenRef = useRef(null)

  const toggleFullscreen = useCallback(() => {
    if (!fullscreenRef.current) return

    if (!document.fullscreenElement) {
      fullscreenRef.current.requestFullscreen?.() || fullscreenRef.current.webkitRequestFullscreen?.()
    } else {
      document.exitFullscreen?.() || document.webkitExitFullscreen?.()
    }
  }, [])

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement))
    }

    document.addEventListener('fullscreenchange', onFullscreenChange)
    document.addEventListener('webkitfullscreenchange', onFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange)
    }
  }, [])

  useEffect(() => {
    setTitle(translatedTitle)
    togglePaddings(true)
    toggleScrollbars(true)

    return () => {
      withBreadcrumbs([])
    }
  }, [translatedTitle])

  const fetchData = useCallback(async (parentId) => {
    setSummary(null)

    const nodes = await StatisticsPresenter.nodesCommon(parentId)
    const summary = await StatisticsPresenter.nodesSummary(parentId)

    setSummary(summary)

    const crumbs = parentId ? await NodePresenter.parents(parentId) : await NodePresenter.tree(({ depth: 500 }))

    function nodesToCrumbs(nodes) {
      return nodes.map(({ id, name, type, parentId, children }) => ({
        id,
        type,
        disabled: false,
        title: name,
        path: parentId,
        ...(children && children.length ? {
          children: nodesToCrumbs(children),
        } : {}),
      }))
    }

    const breadcrumbs = nodesToCrumbs(crumbs)

    if (isMounted.current) {
      setCrumbs(breadcrumbs)
    }

    return {
      nodes,
      summary,
    }
  }, [])

  useEffect(() => {
    if (!tree.length) return

    if (route === 'production') {
      setTimeout(() => setRoute(['production', tree[0].nodeId].join('/')), 50)
      return
    }

    // withBreadcrumbs(crumbs, (crumb, isContext) => {
    //   if (isContext || !crumb.path) {
    //     const newRoute = crumb.path ? ['production', crumb.id].join('/') : 'production'
    //     if (newRoute === route) {
    //       return
    //     }
    //
    //     setTimeout(() => {
    //       setRoute(newRoute)
    //     }, 50) // Wait until modal close
    //     return
    //   }
    //
    //   return async () => {
    //     const data = await NodePresenter.read({
    //       f: {
    //         parentId: {
    //           eq: crumb.path,
    //         },
    //       },
    //     })
    //
    //     if (data.length === 1) {
    //       // TODO force redirect to node!
    //     }
    //
    //     return data.map(({ id, name, type, parentId, available }) => ({
    //       id,
    //       type,
    //       title: name,
    //       path: parentId,
    //       disabled: !available,
    //     }))
    //   }
    // })

    withBreadcrumbs(crumbs, (crumb, isContext) => {
      if (isContext || !crumb.path || !crumb.children) {
        const newRoute = crumb.path ? ['production', crumb.id].join('/') : 'production'
        if (newRoute === route) {
          return
        }

        setTimeout(() => {
          setRoute(newRoute)
        }, 50) // Wait until modal close

        return
      }

      return crumb.children

      // return async () => {
      //   const data = await NodePresenter.read({
      //     f: {
      //       parentId: {
      //         eq: crumb.path,
      //       },
      //     },
      //   })
      //
      //   if (data.length === 1) {
      //     // TODO force redirect to node!
      //   }
      //
      //   return data.map(({ id, name, type, parentId, available }) => ({
      //     id,
      //     type,
      //     title: name,
      //     path: parentId,
      //     disabled: !available,
      //   }))
    })
  }, [withBreadcrumbs, crumbs, route, tree])

  if (!tree.length) return <p>Loading...</p>

  return <>
    <div className={ 'production-summary-cards-wrapper' }>
      {/*<div className={ 'gas-and-oil-card map-container' }>*/ }
      {/*  <GLMap id={ 'production' } />*/ }
      {/*</div>*/ }

      <div>
        { 'bfbcde48-5adb-4692-98c2-7d0511dd3817' === window.location.href.split('/').pop() && <div
          ref={ fullscreenRef }
          className={ 'scada-card' }
        >
          <Paper elevation={ 1 }>
          <Header
            title={ <div style={ { textAlign: 'start' } }>
              <b><Icon filled style={ { marginRight: '0.8rem' } }>full_stacked_bar_chart</Icon></b>
              <span>SCADA</span>
            </div> }
            subtitle={ 'Real time chart' }
          />
          <div className={ 'scada-chart-area' } style={ { height: '50rem', position: 'relative' } }>
            <div style={ { position: 'absolute', right: '0.8rem', top: '0.8rem', zIndex: 2 } }>
              <Button
                flat
                style={ {
                  width: '4rem',
                  height: '4rem',
                } }
                icon={ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }
                onClick={ toggleFullscreen }
              />
            </div>

            <SCADAView
              autoFitToBounds
              maxPipesPerFrame={ 1000 }
              minimalEffectsPipeCount={ 800 }
              minimalEffectsZoomRatio={ 0.35 }
              lodRatioThreshold={ 0.5 }
              diagram={ schema }
              // nodeData={ nodeData }
              // debug={ isDebug }
              theme={ 'industrial' }
              enablePan
              coordinateSystem={ {
                mode: 'isometric',
              } }
              width={ 1024 }
            />
          </div>
          </Paper>
        </div> }
      </div>

      { !!summary && <>
        <ProductionSummaryCard path={ last(breadcrumb) } summary={ summary }/>
      </> }
    </div>
    <div className={ 'production-cards-wrapper' }>
      <ProductionCards
        showError
        showSpinner
        errorClassName={ 'gas-and-oil-card full-width' }
        request={ fetchData }
      >
        <El urn={ '*' }/>
      </ProductionCards>
    </div>
  </>
}
